import { Component, computed, effect, inject, Signal } from '@angular/core';
import {
  DATE_FORMAT,
  EEntityType,
  ERelationType,
  EStatusChipType,
  IExtendedParsedOrder,
  ILogpayPaymentMethod,
  INormalText,
  IParsedBooking,
  IParsedOrder,
  isNoFurtherInThePastThan,
  ONE_HOUR,
  PaymentMethod,
  paymentTypeToRelationType,
  SEVEN_DAYS,
  THIRTY_DAYS,
  TIME_FORMAT_SHORT,
  TWENTY_FOUR_HOURS,
} from '@web/dtos';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { TextInputComponent } from '../../../components/data-input/form-inputs/text-input/text-input.component';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { TableCellComponent } from '../../../components/data-display/table/table-cell/table-cell.component';
import {
  ESortOrder,
  ITableSortParams,
  TableComponent,
  TableSortOrder,
  toSortOrder,
} from '../../../components/data-display/table/table.component';
import { TableHeaderComponent } from '../../../components/data-display/table/table-header/table-header.component';
import { DatePipe } from '@angular/common';
import { DecimalsToCentsPipe } from '../../../pipes/decimals-to-cents/decimals-to-cents.pipe';
import { CentsToEurosPipe } from '../../../pipes/cents-to-euros/cents-to-euros.pipe';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { DropDownComponent } from '../../../components/data-input/form-inputs/drop-down/drop-down.component';
import { BookingsOverviewComponent } from '../../../components/data-display/bookings-overview/bookings-overview.component';
import { OrderStatusChipComponent } from '../../../components/data-display/order-status-chip/order-status-chip.component';
import { AllOrdersStore } from '../../../services/api/orders/all-orders-store.service';

const MONTH = 'month';
const ALL = 'all';

@Component({
  selector: 'aw-orders-page',
  imports: [
    LoadingAnimationComponent,
    TextInputComponent,
    ReactiveFormsModule,
    AppendTestIdPipe,
    TableCellComponent,
    TableComponent,
    TableHeaderComponent,
    DatePipe,
    DropDownComponent,
    BookingsOverviewComponent,
    OrderStatusChipComponent,
  ],
  templateUrl: './orders-page.component.html',
  styles: ``,
})
export class OrdersPageComponent {
  private readonly allOrdersStore: AllOrdersStore = inject(AllOrdersStore);
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  private readonly textStore: WebTextStore = inject(WebTextStore);

  private readonly languagePipe: LanguagePipe = new LanguagePipe();
  private readonly filterTextPipe: FilterTextsPipe = new FilterTextsPipe();
  private readonly decimalsToCentsPipe: DecimalsToCentsPipe =
    new DecimalsToCentsPipe();
  private readonly centsToEurosPipe: CentsToEurosPipe = new CentsToEurosPipe();

  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly EStatusChipType: typeof EStatusChipType = EStatusChipType;
  protected readonly DATE_FORMAT: string = DATE_FORMAT;
  protected readonly TIME_FORMAT_SHORT: string = TIME_FORMAT_SHORT;
  protected readonly timeFilterOptions: {
    name: string;
    value: string | number;
  }[] = [
    { name: 'Letzte Stunde', value: ONE_HOUR },
    { name: 'Letzte 24 Stunden', value: TWENTY_FOUR_HOURS },
    { name: 'Letzte 7 Tage', value: SEVEN_DAYS },
    { name: 'Aktueller Monat', value: MONTH },
    { name: 'Letzte 30 Tage', value: THIRTY_DAYS },
    { name: 'Alle Einträge', value: ALL },
  ];

  protected readonly texts: Signal<INormalText[]> = this.textStore.texts;
  protected readonly orders: Signal<IParsedOrder[]> =
    this.allOrdersStore.allOrders;
  protected readonly isLoading: Signal<boolean> = this.allOrdersStore.isLoading;
  protected readonly openDetails: Record<string, boolean> = {};
  protected readonly shownOrders: Signal<IExtendedParsedOrder[]> = computed(
    (): IExtendedParsedOrder[] => {
      return this.allOrdersStore
        .allOrders()
        .map((order: IParsedOrder) => {
          const priceInCents: number = order.purchase?.totalSum
            ? this.decimalsToCentsPipe.transform(order.purchase!.totalSum!)
            : this.calculateTotalPrice(order.bookings);
          return {
            ...order,
            paymentMethodName: this.resolvePaymentMethod(order.paymentMethod),
            totalPrice: {
              priceInCents,
              formattedPrice: this.centsToEurosPipe.transform(priceInCents),
            },
          };
        })
        .filter((order: IParsedOrder) =>
          JSON.stringify(order)
            .toLowerCase()
            .includes(this.searchValue()?.toLowerCase() ?? '')
        )
        .filter((order: IParsedOrder) => {
          const filterValue = this.timeFilterValue();
          const orderDate = new Date(order.createdAt);
          switch (filterValue) {
            case ONE_HOUR:
              return isNoFurtherInThePastThan(orderDate, ONE_HOUR);
            case TWENTY_FOUR_HOURS:
              return isNoFurtherInThePastThan(orderDate, TWENTY_FOUR_HOURS);
            case SEVEN_DAYS:
              return isNoFurtherInThePastThan(orderDate, SEVEN_DAYS);
            case THIRTY_DAYS:
              return isNoFurtherInThePastThan(orderDate, THIRTY_DAYS);
          }
          if (filterValue === MONTH) {
            const today = new Date();
            const currentMonth: number = today.getMonth();
            const currentYear: number = today.getFullYear();
            return (
              orderDate.getMonth() === currentMonth &&
              orderDate.getFullYear() === currentYear
            );
          }
          return true;
        })
        .sort((a: IParsedOrder, b: IParsedOrder): number => {
          return a.createdAt < b.createdAt ? 1 : 0;
        });
    }
  );
  protected readonly orderRowKey = (order: IExtendedParsedOrder): string =>
    order.id;
  protected readonly searchControl: FormControl<string> =
    this.formBuilder.nonNullable.control('');
  protected readonly timeFrameControl: FormControl<string | number> =
    this.formBuilder.nonNullable.control(TWENTY_FOUR_HOURS);

  protected readonly searchValue: Signal<string | undefined> = toSignal(
    this.searchControl.valueChanges
  );
  protected readonly timeFilterValue: Signal<string | number | undefined> =
    toSignal(this.timeFrameControl.valueChanges);

  constructor() {
    effect(() => {
      this.orders().forEach((order: IParsedOrder): void => {
        this.openDetails[order.id] = false;
      });
      this.timeFrameControl.updateValueAndValidity();
    });
  }

  protected calculateTotalPrice(orderData: IParsedBooking[] = []): number {
    const total: number = orderData.reduce(
      (acc, curr) => acc + (curr.grossPrice ?? 0),
      0
    );
    return total ? total : 0;
  }

  protected resolvePaymentMethod(
    paymentMethod: Partial<PaymentMethod>
  ): string {
    const relationType: ERelationType | undefined = (
      paymentMethod as ILogpayPaymentMethod
    )?.type
      ? paymentTypeToRelationType((paymentMethod as ILogpayPaymentMethod).type)
      : undefined;

    if (relationType) {
      const translatedPayment: string = this.languagePipe.transform(
        this.filterTextPipe.transform(
          this.textStore.texts(),
          relationType,
          EEntityType.directTicket
        )
      );
      if (translatedPayment) {
        return translatedPayment;
      }
    }

    return (paymentMethod as ILogpayPaymentMethod)?.type || '_';
  }

  protected toggleDetails(orderId: string): void {
    this.openDetails[orderId] = !this.openDetails[orderId];
  }

  public orderDateFilter: ITableSortParams<IExtendedParsedOrder>['compare'] = (
    a: IExtendedParsedOrder,
    b: IExtendedParsedOrder,
    order: TableSortOrder
  ): number => {
    const sortDirection = toSortOrder(order);
    return a.createdAt > b.createdAt ? 1 * sortDirection : -1 * sortDirection;
  };

  public orderDataFilter: ITableSortParams<IExtendedParsedOrder>['compare'] = (
    a: IExtendedParsedOrder,
    b: IExtendedParsedOrder,
    order: TableSortOrder,
    attribute: string
  ): number => {
    const sortDirection: ESortOrder = toSortOrder(order);
    // @ts-expect-error-message need to access attributes irrespective of type
    return a[attribute] > b[attribute] ? 1 * sortDirection : -1 * sortDirection;
  };

  public orderTotalPriceFilter: ITableSortParams<IExtendedParsedOrder>['compare'] =
    (
      a: IExtendedParsedOrder,
      b: IExtendedParsedOrder,
      order: TableSortOrder
    ): number => {
      const sortDirection: ESortOrder = toSortOrder(order);

      return +a.totalPrice.priceInCents > +b.totalPrice.priceInCents
        ? 1 * sortDirection
        : -1 * sortDirection;
    };
}
